import { forwardRef, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { MIN_CROP_SIDE, type CropRect } from '../lib/frame'

// Free cropping for a full-width image: the whole photo, with a crop box the
// author drags — inside to move it, a corner or an edge to resize it to any
// shape. The rectangle is in 0..1 of the image; ImageEditor turns it into a
// frame with cropToFrame (lib/frame.ts).

type Edge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
type Grip = Edge | 'move'

interface Props {
  src: string
  /** The image's width / height once measured; the stage takes this shape. */
  natural: number | null
  /** null until the image has loaded. */
  rect: CropRect | null
  onChange(rect: CropRect): void
  onLoad(img: HTMLImageElement): void
  /** CSS max height for the stage, so the dialog's other parts stay on screen. */
  maxHeight: string
  maxWidth: number
  describedBy?: string
}

/** No side thinner than this (0..1), so a handle can always be grabbed again. */
const MIN_SIDE = 0.04
/** safeFrame's limits on a frame's shape (width / height). */
const MIN_SHAPE = 0.06
const MAX_SHAPE = 19

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))

/** Resize or move `r` by (dx, dy) in 0..1 units, held inside the image. */
function adjust(r: CropRect, grip: Grip, dx: number, dy: number): CropRect {
  if (grip === 'move') return { ...r, x: clamp(r.x + dx, 0, 1 - r.w), y: clamp(r.y + dy, 0, 1 - r.h) }
  let { x, y } = r
  let right = r.x + r.w
  let bottom = r.y + r.h
  if (grip.includes('w')) x = clamp(x + dx, 0, right - MIN_SIDE)
  if (grip.includes('e')) right = clamp(right + dx, x + MIN_SIDE, 1)
  if (grip.includes('n')) y = clamp(y + dy, 0, bottom - MIN_SIDE)
  if (grip.includes('s')) bottom = clamp(bottom + dy, y + MIN_SIDE, 1)
  return { x, y, w: right - x, h: bottom - y }
}

/** Whether a frame can hold this crop: zoom tops out (longer side), and the shape stays sane. */
function allowed(r: CropRect, natural: number): boolean {
  const shape = (r.w / r.h) * natural
  return Math.max(r.w, r.h) >= MIN_CROP_SIDE - 1e-9 && shape >= MIN_SHAPE && shape <= MAX_SHAPE
}

const HANDLES: Array<{ edge: Edge; cls: string; cursor: string }> = [
  { edge: 'nw', cls: '-left-2 -top-2', cursor: 'nwse-resize' },
  { edge: 'ne', cls: '-right-2 -top-2', cursor: 'nesw-resize' },
  { edge: 'sw', cls: '-bottom-2 -left-2', cursor: 'nesw-resize' },
  { edge: 'se', cls: '-bottom-2 -right-2', cursor: 'nwse-resize' },
  { edge: 'n', cls: '-top-2 left-1/2 -translate-x-1/2', cursor: 'ns-resize' },
  { edge: 's', cls: '-bottom-2 left-1/2 -translate-x-1/2', cursor: 'ns-resize' },
  { edge: 'w', cls: '-left-2 top-1/2 -translate-y-1/2', cursor: 'ew-resize' },
  { edge: 'e', cls: '-right-2 top-1/2 -translate-y-1/2', cursor: 'ew-resize' },
]

export const CropBox = forwardRef<HTMLDivElement, Props>(function CropBox(
  { src, natural, rect, onChange, onLoad, maxHeight, maxWidth, describedBy },
  ref,
) {
  const stage = useRef<HTMLDivElement | null>(null)
  /** The photo's own area: drags are measured against it. */
  const area = useRef<HTMLDivElement | null>(null)
  const drag = useRef<{ grip: Grip; x: number; y: number; start: CropRect } | null>(null)
  const [active, setActive] = useState<Grip | null>(null)
  const aspect = natural ?? 3 / 2

  const setRefs = (el: HTMLDivElement | null) => {
    stage.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) ref.current = el
  }

  const start = (grip: Grip) => (e: PointerEvent<HTMLElement>) => {
    if (!rect || !natural || (e.pointerType === 'mouse' && e.button !== 0)) return
    e.preventDefault()
    e.stopPropagation()
    stage.current?.focus({ preventScroll: true })
    stage.current?.setPointerCapture?.(e.pointerId)
    drag.current = { grip, x: e.clientX, y: e.clientY, start: rect }
    setActive(grip)
  }

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current
    const box = area.current?.getBoundingClientRect()
    if (!d || !box || !natural || box.width === 0 || box.height === 0) return
    const next = adjust(d.start, d.grip, (e.clientX - d.x) / box.width, (e.clientY - d.y) / box.height)
    if (allowed(next, natural)) onChange(next)
  }

  const end = () => {
    drag.current = null
    setActive(null)
  }

  // Arrows move the box; with Shift they grow or shrink it from its bottom-right corner.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!rect || !natural || e.altKey || e.ctrlKey || e.metaKey) return
    const step = 0.01
    const keys: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    }
    const k = keys[e.key]
    if (!k) return
    e.preventDefault()
    const next = adjust(rect, e.shiftKey ? 'se' : 'move', k[0], k[1])
    if (allowed(next, natural)) onChange(next)
  }

  const pct = (n: number) => `${n * 100}%`
  const place = rect ? { left: pct(rect.x), top: pct(rect.y), width: pct(rect.w), height: pct(rect.h) } : undefined
  return (
    // Padded, so handles on the photo's edges sit fully on the stage and can be grabbed.
    <div
      ref={setRefs}
      role="application"
      aria-label="Crop area"
      aria-describedby={describedBy}
      tabIndex={0}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onLostPointerCapture={end}
      onKeyDown={onKeyDown}
      className="flex touch-none select-none justify-center rounded-2xl p-3 outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <div
        ref={area}
        data-crop-area=""
        style={{ aspectRatio: String(aspect), width: `min(100%, ${maxWidth}px, calc(${maxHeight} * ${aspect}))` }}
        className="relative bg-foreground/8"
      >
        {/* The photo and the shade outside the crop, clipped to the photo. */}
        <div className="absolute inset-0 overflow-hidden rounded-[3px]">
          <img
            src={src}
            alt=""
            draggable={false}
            onLoad={(e) => onLoad(e.currentTarget)}
            className={`pointer-events-none absolute inset-0 block size-full object-fill ${natural ? '' : 'opacity-0'}`}
          />
          {place && natural && <div aria-hidden style={place} className="pointer-events-none absolute shadow-[0_0_0_9999px_rgba(45,27,30,.55)]" />}
        </div>
        {rect && natural && (
          <div
            data-crop-box=""
            onPointerDown={start('move')}
            style={place}
            className={`absolute outline outline-[1.5px] outline-paper ${active === 'move' ? 'cursor-grabbing' : 'cursor-grab'}`}
          >
            {/* Rule of thirds inside the crop, stronger while adjusting. */}
            <div aria-hidden className={`pointer-events-none absolute inset-0 transition-opacity ${active ? 'opacity-100' : 'opacity-40'}`}>
              <span className="absolute inset-y-0 left-1/3 w-px bg-card/70" />
              <span className="absolute inset-y-0 left-2/3 w-px bg-card/70" />
              <span className="absolute inset-x-0 top-1/3 h-px bg-card/70" />
              <span className="absolute inset-x-0 top-2/3 h-px bg-card/70" />
            </div>
            {HANDLES.map((h) => (
              <span
                key={h.edge}
                data-crop-handle={h.edge}
                aria-hidden
                onPointerDown={start(h.edge)}
                style={{ cursor: h.cursor }}
                // A generous invisible hit area around a small visible grip (fingers too).
                className={`absolute flex size-4 items-center justify-center before:absolute before:-inset-2.5 before:content-[''] ${h.cls}`}
              >
                <span
                  className={`block rounded-[2px] bg-card shadow-[0_0_0_1px_rgba(45,27,30,.35)] ${
                    h.edge.length === 2 ? 'size-3' : h.edge === 'n' || h.edge === 's' ? 'h-1.5 w-5' : 'h-5 w-1.5'
                  }`}
                />
              </span>
            ))}
          </div>
        )}
        {!natural && (
          <p className="pointer-events-none absolute inset-0 m-0 flex items-center justify-center font-display text-[14px] italic text-muted-foreground">
            Loading image…
          </p>
        )}
      </div>
    </div>
  )
})
